import {
  Alert,
  Button,
  Container,
  Divider,
  Group,
  Loader,
  Modal,
  Progress,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconAlertCircle, IconGitBranch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../api/client";
import { DecisionTreePanel } from "../components/DecisionTreePanel";
import { ResultView } from "../components/ResultView";
import { RunActions } from "../components/RunActions";
import { TaskForm } from "../components/TaskForm";

export function RunPage() {
  const { runId } = useParams();
  const [branchOpen, { open: openBranch, close: closeBranch }] = useDisclosure(false);
  const [elapsedSec, setElapsedSec] = useState(0);

  // Polling: пока status='pending', refetch каждые 3 сек.
  const { data, isLoading, error } = useQuery({
    queryKey: ["run", runId],
    queryFn: () => api.getRun(runId!),
    enabled: Boolean(runId),
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status === "pending" ? 3000 : false;
    },
    refetchIntervalInBackground: true,
  });

  // Тикающий counter для UX «прогон идёт N секунд» — обновляется каждую секунду
  // пока pending, сбрасывается при загрузке другого прогона.
  useEffect(() => {
    if (data?.status !== "pending") {
      setElapsedSec(0);
      return;
    }
    const start = Date.now();
    const id = window.setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [data?.status, runId]);

  if (isLoading) {
    return (
      <Container size="md">
        <Stack align="center" py="xl">
          <Loader />
          <Text c="dimmed">Загружаю прогон…</Text>
        </Stack>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container size="md">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Не удалось загрузить прогон"
          color="red"
        >
          {error ? String(error) : "Прогон не найден"}
        </Alert>
      </Container>
    );
  }

  const date = new Date(data.state.created_at);

  // --- Pending UI ---
  if (data.status === "pending") {
    // Ожидаемое время: ~90-150 сек. Прогресс в стиле «бесконечный, но
    // указывающий на жизнь» — Mantine Progress с animate.
    const pct = Math.min(95, (elapsedSec / 150) * 100);
    return (
      <Container size="md">
        <Stack gap="lg" py="xl">
          <Stack gap={4}>
            <Title order={2}>Анализирую задачу…</Title>
            <Text c="dimmed">
              Pipeline идёт ~90-150 секунд. Можно открыть другой прогон или новую
              задачу — этот продолжит считаться в фоне, обновится в истории.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Group gap="sm">
              <Loader size="sm" />
              <Text size="sm">Прошло {elapsedSec}с</Text>
            </Group>
            <Progress value={pct} animated striped color="indigo" size="md" />
          </Stack>

          <Stack gap={4}>
            <Text fw={500}>Задача:</Text>
            <Text c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
              {data.state.task}
            </Text>
          </Stack>
        </Stack>
      </Container>
    );
  }

  // --- Failed UI ---
  if (data.status === "failed") {
    return (
      <Container size="md">
        <Stack gap="lg" py="xl">
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Pipeline упал"
            color="red"
            variant="light"
          >
            <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
              {data.error ?? "Неизвестная ошибка"}
            </Text>
          </Alert>
          <Stack gap={4}>
            <Text fw={500}>Задача:</Text>
            <Text c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
              {data.state.task}
            </Text>
          </Stack>
        </Stack>
      </Container>
    );
  }

  // --- Completed: обычный view ---
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 60fr) minmax(0, 40fr)",
        gap: 24,
        height: "calc(100vh - 92px)",
      }}
    >
      <div style={{ overflowY: "auto", paddingRight: 8 }}>
        <Container size="md" px={0}>
          <Stack gap="lg">
            <Stack gap={4}>
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Title order={2}>
                  Прогон от{" "}
                  {date.toLocaleString("ru-RU", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Title>
                <Group gap="xs" wrap="nowrap">
                  <Button
                    leftSection={<IconGitBranch size={16} />}
                    variant="light"
                    onClick={openBranch}
                    size="xs"
                  >
                    Ветвь / уточнить
                  </Button>
                  <RunActions state={data.state} />
                </Group>
              </Group>
              <Text fw={500}>Задача:</Text>
              <Text c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
                {data.state.task}
              </Text>
            </Stack>

            <ResultView
              state={data.state}
              costUsd={data.cost_usd > 0 ? data.cost_usd : undefined}
              durationSec={data.duration_sec > 0 ? data.duration_sec : undefined}
            />
          </Stack>
        </Container>
      </div>

      <div style={{ overflow: "hidden" }}>
        <DecisionTreePanel currentRunId={data.run_id} />
      </div>

      <Modal
        opened={branchOpen}
        onClose={closeBranch}
        title="Создать ветку — уточнить условия и пересчитать"
        size="lg"
      >
        <Text size="sm" c="dimmed" mb="md">
          Введи новую формулировку задачи (с уточнениями относительно текущей).
          Новый узел появится в дереве справа как ребёнок текущего.
        </Text>
        <Divider mb="md" />
        <TaskForm parentRunId={data.run_id} initialTask={data.state.task} />
      </Modal>
    </div>
  );
}
